import React, {
    useState,
    useCallback,
    createContext,
    useContext
} from 'react';
import {
    CheckCircle,
    XCircle,
    AlertTriangle,
    Info,
    X
} from 'lucide-react';

const ToastContext = createContext(null);

export const useToast = () => {
    const context = useContext(ToastContext);

    if (!context) {
        throw new Error('useToast must be used within ToastProvider');
    }

    return context;
};

const ToastItem = ({ toast, onClose }) => {
    const { message, type } = toast;

    const config = {
        success: {
            bg: 'bg-green-500/10',
            border: 'border-green-500/30',
            icon: CheckCircle,
            iconColor: 'text-green-400',
            title: 'Успешно',
            textColor: 'text-green-200'
        },
        error: {
            bg: 'bg-red-500/10',
            border: 'border-red-500/30',
            icon: XCircle,
            iconColor: 'text-red-400',
            title: 'Ошибка',
            textColor: 'text-red-200'
        },
        warning: {
            bg: 'bg-yellow-500/10',
            border: 'border-yellow-500/30',
            icon: AlertTriangle,
            iconColor: 'text-yellow-400',
            title: 'Внимание',
            textColor: 'text-yellow-200'
        },
        info: {
            bg: 'bg-blue-500/10',
            border: 'border-blue-500/30',
            icon: Info,
            iconColor: 'text-blue-400',
            title: 'Информация',
            textColor: 'text-blue-200'
        }
    };

    const {
        bg,
        border,
        icon: Icon,
        iconColor,
        title,
        textColor
    } = config[type] || config.info;

    return (
        <div
            className={`${bg} ${border} border backdrop-blur-sm rounded-lg shadow-xl p-4 flex items-start gap-3 pointer-events-auto`}
            style={{ animation: 'slideInRight 0.3s ease-out' }}
        >
            <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />

            <div className="flex-1 min-w-0">
                <div className={`font-semibold text-sm ${textColor}`}>
                    {title}
                </div>

                <div className="text-slate-300 text-sm mt-1 break-words">
                    {message}
                </div>
            </div>

            <button
                onClick={onClose}
                className="text-slate-400 hover:text-white transition p-1 rounded hover:bg-slate-700/50 flex-shrink-0"
                title="Закрыть"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
};

const ToastContainer = ({ toasts, removeToast }) => {
    if (toasts.length === 0) return null;

    return (
        <div className="fixed top-4 right-4 z-[100] space-y-2 max-w-md pointer-events-none">
            {toasts.map((toast) => (
                <ToastItem
                    key={toast.id}
                    toast={toast}
                    onClose={() => removeToast(toast.id)}
                />
            ))}
        </div>
    );
};

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const showToast = useCallback((message, type = 'info', duration = 4000) => {
        const id = Date.now() + Math.random();

        const newToast = {
            id,
            message,
            type,
            duration
        };

        setToasts((prev) => [...prev, newToast]);

        setTimeout(() => {
            setToasts((prev) => prev.filter((toast) => toast.id !== id));
        }, duration);

        return id;
    }, []);

    const removeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    const toast = {
        success: (message, duration) => showToast(message, 'success', duration),
        error: (message, duration) => showToast(message, 'error', duration || 6000),
        warning: (message, duration) => showToast(message, 'warning', duration),
        info: (message, duration) => showToast(message, 'info', duration)
    };

    return (
        <ToastContext.Provider value={toast}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </ToastContext.Provider>
    );
};